import * as React from "react"

import { cn } from "@/lib/utils"
import { sanitizeInputValue } from "@/lib/sanitization"

type InputSanitizeFn = (value: string, options?: { trim?: boolean }) => string

type InputProps = React.ComponentProps<"input"> & {
  sanitize?: InputSanitizeFn | null
  onValueChange?: (value: string) => void
}

const applySanitize = (
  event: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
  sanitize?: InputSanitizeFn | null,
  { trim }: { trim?: boolean } = {}
): string => {
  if (!sanitize) {
    return event.currentTarget.value
  }
  const raw = event.currentTarget.value
  const next = sanitize(raw, { trim })
  if (next !== raw) {
    event.currentTarget.value = next
  }
  return next
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, sanitize = sanitizeInputValue, onChange, onBlur, onValueChange, ...props }, ref) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = applySanitize(event, sanitize, { trim: false })
      onValueChange?.(next)
      onChange?.(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const next = applySanitize(event, sanitize, { trim: true })
      onValueChange?.(next)
      onBlur?.(event)
    }

    return (
      <input
        type={type}
        className={cn(
          "flex h-11 w-full rounded-lg border border-border bg-bg-0 px-3 py-2 text-sm text-text-900 transition-all duration-150 placeholder:text-text-500 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-text-900 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=error]:border-brand-red data-[state=error]:bg-rose-surface data-[state=error]:text-text-900 data-[state=error]:focus-visible:ring-brand-red/60 data-[state=success]:border-brand-green data-[state=success]:bg-brand-green/10 data-[state=success]:focus-visible:ring-brand-green/35 aria-[invalid=true]:border-brand-red aria-[invalid=true]:bg-rose-surface aria-[invalid=true]:focus-visible:ring-brand-red/60 md:text-sm",
          className
        )}
        ref={ref}
        onChange={handleChange}
        onBlur={handleBlur}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
